import React from 'react';

import * as yup from 'yup';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import { Link } from 'react-router-dom';
import { toastError } from 'react-geek-toast';
import ReCAPTCHA from 'react-google-recaptcha';

import FormInput from 'partial/form/FormInput';
import Button from 'partial/components/Button';
import { setIsLoading } from 'utils/reqq-helper';
import FormPassword from 'partial/form/FormPassword';
import logo from 'assets/images/logo-horizontal.svg';
import { useCreateModal } from 'partial/components/Modal';
import { HiOutlineMail } from 'react-icons/hi';
import Form, {
  ErrorLabel,
  ErrorOrDescription,
  setError422,
} from 'partial/form/Form';

import { useRegister } from '../hooks';
import { REGISTER } from '../constants';
import PrivacyPolicyModal from '../modals/PrivacyPolicyModal';
import TermsAndConditionModal from '../modals/TermsAndConditionModal';

const initialValues = {
  email_address: '',
  company_name: '',
  first_name: '',
  last_name: '',
  password: '',
  password_confirmation: '',
  terms_and_condition: false,
};

const required = 'This field is required';

const validationSchema = yup.object().shape({
  email_address: yup
    .string()
    .email('The email format is invalid')
    .required(required),
  company_name: yup.string().required(required),
  first_name: yup.string().required(required),
  last_name: yup.string().required(required),
  password: yup
    .string()
    .required(required)
    .min(8, 'At least 8 characters')
    .matches(/[A-Z]/, 'Must contain 1 uppercase letter')
    .matches(/[a-z]/, 'Must contain 1 lowercase letter')
    .matches(/[0-9]/, 'Must contain 1 numeric digit')
    .matches(/^(?=.*[!@#$%^&*])/, 'At least 1 special character (!@#$%&*)'),
  password_confirmation: yup
    .string()
    .required(required)
    .oneOf([yup.ref('password'), ''], 'Password does not match'),
  terms_and_condition: yup
    .bool()
    .oneOf([true], 'You must agree and accept the terms and privacy policy'),
});

const ENABLE_CAPTCHA = true;

function SignUpForm({ onSuccess }) {
  const recaptchaRef = React.useRef();
  const [isLoading, register] = useRegister();
  const [validateOnChange, setValidateOnChange] = React.useState(false);
  const { values, setFieldValue, handleSubmit, errors } = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (form, { setFieldError }) => {
      try {
        setIsLoading('post', REGISTER, true);
        const payload = form;
        if (ENABLE_CAPTCHA) {
          payload.captcha = await recaptchaRef.current.executeAsync();
        }
        register(
          payload,
          (data) => {
            onSuccess({ ...payload, ...data });
          },
          (err) => {
            setError422(err, setFieldError);
            recaptchaRef.current.reset();
          }
        );
      } catch (err) {
        toastError('An error occurred! Please try again');
        setIsLoading('post', REGISTER, false);
        recaptchaRef.current.reset();
      }
    },
    validateOnChange,
  });
  const createModal = useCreateModal();

  const openPrivacyModal = React.useCallback(
    (e) => {
      e.preventDefault();
      createModal({
        content: (close) => <PrivacyPolicyModal onClose={close} />,
      });
    },
    [createModal]
  );

  const openTermsOfServiceModal = React.useCallback(
    (e) => {
      e.preventDefault();
      createModal({
        content: (close) => <TermsAndConditionModal onClose={close} />,
      });
    },
    [createModal]
  );

  return (
    <>
      <div className="flex justify-center relative">
        <img src={logo} className="h-14 mx-auto mb-5" alt="App" />
        <p className="absolute bottom-5 left-60 ml-2 text-xs text-primary-800 font-medium whitespace-nowrap">
          BILLER PORTAL
        </p>
      </div>
      <div className="text-center mb-10">
        <div className="text-2xl text-gray-900 font-semibold mb-0 text-center">
          Sign up for free
        </div>
      </div>
      <Form
        error={errors}
        noValidate
        autoComplete="off"
        onSubmit={handleSubmit}
        className="space-y-3"
      >
        <div>
          <FormInput
            name="company_name"
            label="Organization Name"
            type="text"
            required
            placeholder="Enter Organization Name"
            onSetFieldValue={setFieldValue}
            value={values?.company_name}
            error={errors?.company_name}
          />
          <ErrorLabel name="company_name" />
        </div>
        <div>
          <div className="relative z-10">
            <FormInput
              name="first_name"
              label="First Name"
              type="text"
              required
              placeholder="Enter First Name"
              onSetFieldValue={setFieldValue}
              value={values?.first_name}
              error={errors?.first_name}
            />
          </div>
          <ErrorLabel name="first_name" />
        </div>
        <div>
          <div className="relative z-10">
            <FormInput
              name="last_name"
              label="Last Name"
              type="text"
              required
              placeholder="Enter Last Name"
              onSetFieldValue={setFieldValue}
              value={values?.last_name}
              error={errors?.last_name}
            />
          </div>
          <ErrorLabel name="last_name" />
        </div>
        <div>
          <div className="relative z-10">
            <FormInput
              icon={HiOutlineMail}
              name="email_address"
              label="Email Address"
              type="email"
              required
              placeholder="Enter Email Address"
              onSetFieldValue={setFieldValue}
              value={values?.email_address}
              error={errors?.email_address}
            />
          </div>
          <ErrorOrDescription
            name="email_address"
            label="You will use this email address to sign in to your new DST Biller Portal account."
          />
        </div>
        <div>
          <FormPassword
            name="password"
            label="Password"
            required
            placeholder="Enter password"
            onSetFieldValue={setFieldValue}
            value={values?.password}
            error={errors?.password}
          />
          <ErrorLabel name="password" />
        </div>
        <div>
          <FormPassword
            name="password_confirmation"
            label="Confirm Password"
            required
            placeholder="Confirm password"
            onSetFieldValue={setFieldValue}
            value={values?.password_confirmation}
            error={errors?.password_confirmation}
          />
          <ErrorLabel name="password_confirmation" />
        </div>
        <div>
          <div className="">
            <div className="relative flex items-start">
              <div className="flex items-center h-5">
                <input
                  id="agreement"
                  aria-describedby="agreement-description"
                  name="accept-terms"
                  type="checkbox"
                  className="form-checkbox h-5 w-5 rounded mt-1"
                  onChange={({ target }) =>
                    setFieldValue('terms_and_condition', target.checked)
                  }
                  checked={values?.terms_and_condition}
                />
              </div>
              <div className="ml-3 text-gray-600">
                <label htmlFor="agreement">
                  By selecting this checkbox, I agree and accept the{' '}
                  <a href="/" onClick={openTermsOfServiceModal}>
                    DST Terms of Conditions of Services
                  </a>{' '}
                  and{' '}
                  <a href="/" onClick={openPrivacyModal}>
                    Privacy Policy
                  </a>{' '}
                  on behalf of my company.
                </label>
              </div>
            </div>
          </div>
        </div>
        <ErrorLabel name="terms_and_condition" />
        {ENABLE_CAPTCHA ? (
          <ReCAPTCHA
            ref={recaptchaRef}
            size="invisible"
            sitekey={
              process.env.REACT_APP_RECAPTCHA ||
              '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI'
            }
          />
        ) : null}
        <Button
          size="xl"
          primary
          type="submit"
          className="w-full"
          disabled={isLoading}
          onClick={() => setValidateOnChange(true)}
        >
          Create an Organization Account
        </Button>
        <div className="text-center space-x-1 mt-6">
          <span>Already have an account?</span>
          <Link className="inline-block text-primary-800" to="/">
            Login here
          </Link>
        </div>
      </Form>
    </>
  );
}

SignUpForm.defaultProps = {
  onSuccess: null,
};

SignUpForm.propTypes = {
  onSuccess: PropTypes.instanceOf(Object),
};

export default SignUpForm;
